const BASE = process.env.REACT_APP_API_URL || '/api';

// ── Auth token helpers ─────────────────────────────────────────────────────

// Shared SSO cookie set by trustflow.vn on the parent domain (.trustflow.vn),
// so any subdomain (incl. ai.trustflow.vn) can read the logged-in JWT directly
// — no redirect/handoff round-trip needed when a TrustFlow session exists.
function getSharedCookieToken() {
  if (typeof document === 'undefined') return '';
  const c = document.cookie.split(';').map(s => s.trim()).find(s => s.startsWith('auth_token='));
  return c ? decodeURIComponent(c.split('=').slice(1).join('=')) : '';
}

// Clear the shared cookie across the parent domain (best effort — works from a
// subdomain when the cookie was set with domain=.trustflow.vn).
export function clearSharedCookie() {
  if (typeof document === 'undefined') return;
  for (const domain of ['.trustflow.vn', window.location.hostname]) {
    document.cookie = `auth_token=; path=/; domain=${domain}; max-age=0; samesite=lax`;
  }
  document.cookie = 'auth_token=; path=/; max-age=0; samesite=lax';
}

// A JWT is only usable while its `exp` is still in the future (small buffer).
// Checking locally avoids spending a 401 round-trip on a token we know is dead,
// and lets us fall through to a fresher token from the shared cookie.
function isTokenAlive(token) {
  if (!token) return false;
  try {
    // base64url → base64 + padding, so strict atob() doesn't throw
    const seg = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(seg + '='.repeat((4 - (seg.length % 4)) % 4)));
    if (!payload.exp) return true; // no expiry claim — assume valid
    return payload.exp * 1000 > Date.now() + 60_000;
  } catch { return false; }
}

// Mirror a login into the shared .trustflow.vn cookie so the session exists in
// the ONE place both trustflow.vn and ai.trustflow.vn can see. Because the
// cookie is the single cross-origin channel (localStorage is per-origin),
// writing it here is what lets an OTP login on ai participate in SSO — and,
// combined with getToken() below, lets a logout on either property (which
// deletes this cookie) propagate to the other.
function setSharedCookie(token) {
  if (typeof document === 'undefined' || !token) return;
  const onTrustflow = window.location.hostname.endsWith('trustflow.vn');
  const domain = onTrustflow ? '; domain=.trustflow.vn' : '';
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `auth_token=${token}; path=/${domain}; max-age=${30 * 24 * 60 * 60}${secure}; samesite=lax`;
}

// The shared .trustflow.vn cookie is the SOURCE OF TRUTH for the SSO session.
// If it is present and alive we are logged in (mirror it locally for fast
// reads); if it is gone, the session ended — on this or another TrustFlow
// property — so we do NOT fall back to a stale local token. This is what makes
// a logout on trustflow.vn force a logout here.
export function getToken() {
  const shared = getSharedCookieToken();
  if (isTokenAlive(shared)) {
    if (localStorage.getItem('tf_token') !== shared) localStorage.setItem('tf_token', shared);
    return shared;
  }
  if (localStorage.getItem('tf_token')) localStorage.removeItem('tf_token');
  return '';
}
export function setToken(t)      { localStorage.setItem('tf_token', t); setSharedCookie(t); }
export function clearToken()     { localStorage.removeItem('tf_token'); localStorage.removeItem('tf_user'); }
export function getUser()        { try { return JSON.parse(localStorage.getItem('tf_user') || 'null'); } catch { return null; } }
export function setUser(u)       { localStorage.setItem('tf_user', JSON.stringify(u)); }
export function isLoggedIn()     { return !!getToken(); }

// Keep the locally stored credit balance in sync with the server's response
// after every charged action, and notify listeners (header chip).
export function syncCredits(credits) {
  if (credits == null) return;
  try {
    const u = getUser() || {};
    setUser({ ...u, credits });
    window.dispatchEvent(new CustomEvent('tf-credits', { detail: { credits } }));
  } catch { /* ignore */ }
}

// Attaches the user's chosen UI language (see src/i18n.js — same localStorage
// key) as request HEADERS, not a query param — matching trustflow_web's own
// approach (src/lib/http-client.ts there sends `Accept-Language` + a plain
// `language` header, never a query string). This project's earlier bilingual
// attempt used `?tf_lang=` and hit a browser-extension bug where switching to
// EN made data silently fail to load; the extension turned out to be
// hijacking window.fetch globally rather than filtering on the query param
// specifically (see nativeFetch/looksLikeHijackedResponse below, which is the
// actual fix), but moving language off the URL entirely removes any chance
// of it being the trigger and keeps this app's request shape consistent with
// the rest of the TrustFlow ecosystem.
function langHeaders() {
  try {
    const lang = localStorage.getItem('tf_locale');
    if (!lang || lang === 'vi') return {}; // 'vi' is the default the backend already assumes
    return { 'Accept-Language': lang, 'language': lang };
  } catch {
    return {};
  }
}

function authHeaders(extra = {}) {
  const t = getToken();
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}), ...langHeaders(), ...extra };
}

// Every one of our own endpoints returns JSON. A 200 with an HTML body
// (Content-Type: text/html) means the request never reached our API at all
// — confirmed live: the SAME cached HTML came back for every endpoint
// (research/fundraising/meta/technical/...) regardless of URL or query
// params, which is the signature of something having monkey-patched
// window.fetch globally rather than filtering by request pattern (a
// wallet/web3 extension's injected provider script is the usual culprit).
// `nativeFetch` (captured in index.html before any extension's content
// script could run) sidesteps a patched global fetch when available; the
// retry below is a second line of defense for anything that isn't a
// simple monkey-patch (e.g. a timing race in the extension's own setup).
const nativeFetch = (typeof window !== 'undefined' && window.__pristineFetch) || fetch;

function looksLikeHijackedResponse(res) {
  const contentType = res.headers.get('content-type') || '';
  return res.ok && !contentType.includes('application/json');
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function apiFetch(url, opts = {}) {
  let res = await nativeFetch(url, { ...opts, headers: { ...authHeaders(), ...(opts.headers || {}) } });
  if (res.status === 401) {
    clearToken();
    // Redirect to handoff once — sessionStorage flag prevents looping if the
    // token from trustflow.vn keeps failing (avoids infinite redirect cycle).
    if (!sessionStorage.getItem('tf_auth_redirecting')) {
      sessionStorage.setItem('tf_auth_redirecting', '1');
      const callback = encodeURIComponent(window.location.origin + window.location.pathname);
      window.location.href = `https://trustflow.vn/vi/auth/handoff?redirectTo=${callback}`;
    }
    throw new Error('unauthorized');
  }
  sessionStorage.removeItem('tf_auth_redirecting'); // clear flag on any successful call

  // Self-heal transparently: retry a couple of times with a short backoff
  // before ever bothering the user — this alone resolves the vast majority
  // of cases, since the interfering extension's injection window is brief.
  for (let attempt = 0; looksLikeHijackedResponse(res) && attempt < 2; attempt++) {
    await sleep(250 * (attempt + 1));
    res = await nativeFetch(url, { ...opts, headers: { ...authHeaders(), ...(opts.headers || {}) } });
  }

  // Still hijacked after retrying — genuinely persistent interference (e.g.
  // an extension that blocks the request outright rather than racing us).
  // res.json() would otherwise throw an opaque "Unexpected token '<'" deep
  // in a page's silent .catch(() => {}), rendering as a misleading "no
  // data" empty state instead of a real, actionable error. Surface it as a
  // distinct error type + a one-time global banner (see Layout.jsx's
  // 'tf-network-interference' listener) instead.
  if (looksLikeHijackedResponse(res)) {
    window.dispatchEvent(new CustomEvent('tf-network-interference'));
    const err = new Error('Phản hồi không hợp lệ từ máy chủ — có thể do extension trình duyệt can thiệp vào request.');
    err.isNetworkInterference = true;
    throw err;
  }
  return res;
}

// ── Auth API ───────────────────────────────────────────────────────────────

export async function sendOtp(email) {
  const res = await fetch(`${BASE}/auth/otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || 'Không gửi được OTP');
  return data;
}

export async function loginWithOtp(email, otp) {
  const res = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || 'Đăng nhập thất bại');
  setToken(data.token);

  // Fetch full user info (including isPremium)
  const me = await getMe();
  setUser(me);
  return me;
}

export async function getMe() {
  // Use plain fetch — apiFetch would clear the token + reload on 401,
  // causing a redirect loop when session restoration is still in progress.
  const t = getToken();
  const res = await fetch(`${BASE}/auth/me`, {
    headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Phiên đăng nhập không hợp lệ');
  return data;
}

export async function logout() {
  clearToken();
}

// ── Research API ───────────────────────────────────────────────────────────

export async function startResearch(projectName) {
  const res = await apiFetch(`${BASE}/research`, {
    method: 'POST',
    body: JSON.stringify({ projectName }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 402) throw Object.assign(new Error(data.message || 'Không đủ credits'), { insufficientCredits: true });
  if (res.status === 403) throw Object.assign(new Error(data.message || 'Premium required'), { premiumRequired: true });
  if (!res.ok) throw new Error(data.message || data.error || 'Failed to start research');
  syncCredits(data.creditsRemaining);
  return data;
}

export async function listResearch({ status, limit = 20, skip = 0 } = {}) {
  const params = new URLSearchParams({ limit, skip });
  if (status) params.set('status', status);
  const res = await apiFetch(`${BASE}/research?${params}`);
  if (!res.ok) throw new Error('Failed to fetch list');
  return res.json();
}

export async function listDiscovered({ limit = 6, skip = 0 } = {}) {
  const res = await apiFetch(`${BASE}/research/discovered?limit=${limit}&skip=${skip}`);
  if (!res.ok) throw new Error('Không tải được dự án hệ thống khám phá');
  return res.json();
}

export async function getResearch(slug) {
  const res = await apiFetch(`${BASE}/research/${slug}`);
  if (!res.ok) throw new Error('Not found');
  return res.json();
}

export async function pollStatus(slug) {
  const res = await apiFetch(`${BASE}/research/${slug}/status`);
  if (!res.ok) throw new Error('Not found');
  return res.json();
}

export async function refreshResearch(slug) {
  const res = await apiFetch(`${BASE}/research/${slug}/refresh`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (res.status === 402) throw Object.assign(new Error(data.message || 'Không đủ credits'), { insufficientCredits: true });
  if (res.status === 403) throw Object.assign(new Error(data.message || 'Premium required'), { premiumRequired: true });
  if (!res.ok) throw new Error(data.message || 'Failed to refresh');
  syncCredits(data.creditsRemaining);
  return data;
}

export async function deleteResearch(id) {
  const res = await apiFetch(`${BASE}/research/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete');
  return res.json();
}

// ── Knowledge API ──────────────────────────────────────────────────────────

export async function getKnowledge(term, { parentCatId = null, depth = 2, parentTerm = null, ancestors = [] } = {}) {
  const params = new URLSearchParams();
  if (parentCatId) params.set('parent', parentCatId);
  if (depth && depth !== 2) params.set('depth', depth);
  if (parentTerm) params.set('parentTerm', parentTerm);
  if (ancestors.length > 0) params.set('ancestors', ancestors.join(','));
  const qs = params.toString();
  const url = `${BASE}/knowledge/${encodeURIComponent(term)}${qs ? '?' + qs : ''}`;
  const res = await apiFetch(url);
  if (!res.ok) throw new Error('Không tìm thấy định nghĩa');
  return res.json();
}

export async function getKnowledgeTree() {
  const res = await apiFetch(`${BASE}/knowledge/tree`);
  if (!res.ok) throw new Error('Không tải được cây kiến thức');
  return res.json();
}

export async function getKnowledgeChildren(catId) {
  const res = await apiFetch(`${BASE}/knowledge/children/${catId}`);
  if (!res.ok) throw new Error('Không tải được danh sách');
  return res.json();
}

// ── Personnel API ──────────────────────────────────────────────────────────

export async function createPersonnel(data) {
  const res = await apiFetch(`${BASE}/personnel`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 402) throw Object.assign(new Error(body.message || 'Không đủ credits'), { insufficientCredits: true });
  if (!res.ok) throw new Error(body.message || 'Không thể tạo hồ sơ nhân sự');
  syncCredits(body.creditsRemaining);
  return body;
}

export async function listPersonnel(projectSlug) {
  const url = projectSlug ? `${BASE}/personnel?project=${projectSlug}` : `${BASE}/personnel`;
  const res = await apiFetch(url);
  if (!res.ok) throw new Error('Không tải được danh sách nhân sự');
  return res.json();
}

export async function getPersonnel(slug) {
  const res = await apiFetch(`${BASE}/personnel/${slug}`);
  if (!res.ok) throw new Error('Không tìm thấy hồ sơ');
  return res.json();
}

export async function pollPersonnelStatus(slug) {
  const res = await apiFetch(`${BASE}/personnel/${slug}/status`);
  if (!res.ok) throw new Error('Không lấy được trạng thái');
  return res.json();
}

export async function startPersonnelResearch(slug) {
  const res = await apiFetch(`${BASE}/personnel/${slug}/research`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    const err = new Error(data.message || 'Đang trong thời gian chờ');
    err.cooldown = true; err.nextResearchDate = data.nextResearchDate;
    throw err;
  }
  if (res.status === 402) throw Object.assign(new Error(data.message || 'Không đủ credits'), { insufficientCredits: true });
  if (res.status === 403) throw Object.assign(new Error(data.message || 'Premium required'), { premiumRequired: true });
  if (!res.ok) throw new Error(data.message || data.error || 'Không bắt đầu được nghiên cứu');
  syncCredits(data.creditsRemaining);
  return data;
}

export async function reextractPersonnel(slug) {
  const res = await apiFetch(`${BASE}/personnel/${slug}/reextract`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Không trích xuất được điểm');
  return data;
}

export async function deletePersonnel(slug) {
  const res = await apiFetch(`${BASE}/personnel/${slug}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Không thể xóa');
  return res.json();
}

// ── Market / On-chain API ────────────────────────────────────────────────────

// ── Fundraising API ──────────────────────────────────────────────────────

export async function listFundraising({ limit = 20, skip = 0, sort, q, roundType, category, chain, token, minAmount, maxAmount, dateFrom, dateTo } = {}) {
  const params = new URLSearchParams({ limit, skip });
  if (sort) params.set('sort', sort);
  if (q) params.set('q', q);
  if (roundType) params.set('roundType', roundType);
  if (category) params.set('category', category);
  if (chain) params.set('chain', chain);
  if (token) params.set('token', token);
  if (minAmount) params.set('minAmount', minAmount);
  if (maxAmount) params.set('maxAmount', maxAmount);
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  const res = await apiFetch(`${BASE}/fundraising?${params}`);
  if (!res.ok) throw new Error('Không tải được dữ liệu fundraising');
  return res.json();
}

export async function getFundraisingMeta() {
  const res = await apiFetch(`${BASE}/fundraising/meta`);
  if (!res.ok) throw new Error('Không tải được bộ lọc fundraising');
  return res.json();
}

export async function getFundraisingFunds() {
  const res = await apiFetch(`${BASE}/fundraising/funds`);
  if (!res.ok) throw new Error('Không tải được dữ liệu quỹ đầu tư');
  return res.json();
}

export async function getFundraisingCharts() {
  const res = await apiFetch(`${BASE}/fundraising/charts`);
  if (!res.ok) throw new Error('Không tải được dữ liệu biểu đồ');
  return res.json();
}

export async function getFundraisingGroups({ by = 'investor', q, roundType, category, chain, token, minAmount, maxAmount, dateFrom, dateTo } = {}) {
  const params = new URLSearchParams({ by });
  if (q) params.set('q', q);
  if (roundType) params.set('roundType', roundType);
  if (category) params.set('category', category);
  if (chain) params.set('chain', chain);
  if (token) params.set('token', token);
  if (minAmount) params.set('minAmount', minAmount);
  if (maxAmount) params.set('maxAmount', maxAmount);
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  const res = await apiFetch(`${BASE}/fundraising/groups?${params}`);
  if (!res.ok) throw new Error('Không tải được nhóm fundraising');
  return res.json();
}

export async function getMarketAlerts(slug) {
  const url = slug ? `${BASE}/market/alerts?slug=${encodeURIComponent(slug)}` : `${BASE}/market/alerts`;
  const res = await apiFetch(url);
  if (!res.ok) throw new Error('Không tải được cảnh báo thị trường');
  return res.json();
}

export async function getOnchain() {
  const res = await apiFetch(`${BASE}/market/onchain`);
  if (!res.ok) throw new Error('Không tải được dữ liệu on-chain');
  return res.json();
}

export async function getEtfFlowAssets() {
  const res = await apiFetch(`${BASE}/etf-flow/assets`);
  if (!res.ok) throw new Error('Không tải được danh sách tài sản ETF');
  return res.json();
}

export async function getEtfFlows(asset = 'BTC', limit = 90) {
  const res = await apiFetch(`${BASE}/etf-flow?asset=${asset}&limit=${limit}`);
  if (!res.ok) throw new Error('Không tải được dữ liệu dòng tiền ETF');
  return res.json();
}

export async function getEtfFlowSummary(asset = 'BTC') {
  const res = await apiFetch(`${BASE}/etf-flow/summary?asset=${asset}`);
  if (!res.ok) throw new Error('Không tải được tổng hợp dòng tiền ETF');
  return res.json();
}

export async function getNews(params = {}) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v != null)).toString();
  const res = await apiFetch(`${BASE}/news${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error('Không tải được tin tức');
  return res.json();
}

export async function getNewsMeta() {
  const res = await apiFetch(`${BASE}/news/meta`);
  if (!res.ok) throw new Error('Không tải được bộ lọc tin tức');
  return res.json();
}

export async function getFlowGraphHistory() {
  const res = await apiFetch(`${BASE}/flow-graph/history`);
  if (!res.ok) throw new Error('Không tải được lịch sử truy vết');
  return res.json();
}

export async function getFlowGraphSnapshot(id) {
  const res = await apiFetch(`${BASE}/flow-graph/snapshot/${encodeURIComponent(id)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Không mở được lần truy vết này');
  return data;
}

export async function getHackIncidents() {
  const res = await apiFetch(`${BASE}/hack-tracker`);
  if (!res.ok) throw new Error('Không tải được danh sách vụ hack');
  return res.json();
}

export async function getHackIncident(id) {
  const res = await apiFetch(`${BASE}/hack-tracker/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error('Không tải được dữ liệu vụ hack');
  return res.json();
}

export async function getHackLifetime(id) {
  const res = await apiFetch(`${BASE}/hack-tracker/${encodeURIComponent(id)}/lifetime`);
  if (!res.ok) throw new Error('Không tải được mô tả vòng đời vụ hack');
  return res.json();
}

export async function getDominanceHistory(limit = 180) {
  const res = await apiFetch(`${BASE}/dominance?limit=${limit}`);
  if (!res.ok) throw new Error('Không tải được dữ liệu dominance');
  return res.json();
}

export async function getDominanceSummary() {
  const res = await apiFetch(`${BASE}/dominance/summary`);
  if (!res.ok) throw new Error('Không tải được tổng hợp dominance');
  return res.json();
}

// ── Portfolio API ────────────────────────────────────────────────────────

export async function getPortfolioPresets() {
  const res = await apiFetch(`${BASE}/portfolio/presets`);
  if (!res.ok) throw new Error('Không tải được danh sách sàn/ví mẫu');
  return res.json();
}

export async function getPortfolioLocations() {
  const res = await apiFetch(`${BASE}/portfolio/locations`);
  if (!res.ok) throw new Error('Không tải được vị trí cất giữ tài sản');
  return res.json();
}

export async function createPortfolioLocation(data) {
  const res = await apiFetch(`${BASE}/portfolio/locations`, { method: 'POST', body: JSON.stringify(data) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || 'Không thể thêm vị trí cất giữ');
  return body;
}

export async function deletePortfolioLocation(id) {
  const res = await apiFetch(`${BASE}/portfolio/locations/${id}`, { method: 'DELETE' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || 'Không thể xoá vị trí cất giữ');
  return body;
}

export async function searchPortfolioAsset(q) {
  const res = await apiFetch(`${BASE}/portfolio/search-asset?q=${encodeURIComponent(q)}`);
  if (!res.ok) throw new Error('Không tìm được tài sản');
  return res.json();
}

export async function getPortfolioFxRate() {
  const res = await apiFetch(`${BASE}/portfolio/fx-rate`);
  if (!res.ok) throw new Error('Không tải được tỷ giá');
  return res.json();
}

export async function getPortfolioTransactions(symbol) {
  const qs = symbol ? `?symbol=${encodeURIComponent(symbol)}` : '';
  const res = await apiFetch(`${BASE}/portfolio/transactions${qs}`);
  if (!res.ok) throw new Error('Không tải được danh sách giao dịch');
  return res.json();
}

export async function createPortfolioTransaction(data) {
  const res = await apiFetch(`${BASE}/portfolio/transactions`, { method: 'POST', body: JSON.stringify(data) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || 'Không thể tạo giao dịch');
  return body;
}

export async function deletePortfolioTransaction(id) {
  const res = await apiFetch(`${BASE}/portfolio/transactions/${id}`, { method: 'DELETE' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || 'Không thể xoá giao dịch');
  return body;
}

export async function getPortfolioCashflows() {
  const res = await apiFetch(`${BASE}/portfolio/cashflows`);
  if (!res.ok) throw new Error('Không tải được lịch sử nạp/rút');
  return res.json();
}

export async function createPortfolioCashflow(data) {
  const res = await apiFetch(`${BASE}/portfolio/cashflows`, { method: 'POST', body: JSON.stringify(data) });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || 'Không thể tạo lệnh nạp/rút');
  return body;
}

export async function deletePortfolioCashflow(id) {
  const res = await apiFetch(`${BASE}/portfolio/cashflows/${id}`, { method: 'DELETE' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || 'Không thể xoá lệnh nạp/rút');
  return body;
}

export async function getPortfolioSummary() {
  const res = await apiFetch(`${BASE}/portfolio/summary`);
  if (!res.ok) throw new Error('Không tải được tổng hợp portfolio');
  return res.json();
}

export async function getPortfolioAssetStatus(symbol) {
  const res = await apiFetch(`${BASE}/portfolio/asset-status/${encodeURIComponent(symbol)}`);
  if (!res.ok) throw new Error('Không tải được trạng thái tài sản');
  return res.json();
}

export async function getPortfolioTimeline({ before, months = 3 } = {}) {
  const params = new URLSearchParams({ months: String(months) });
  if (before) params.set('before', before);
  const res = await apiFetch(`${BASE}/portfolio/timeline?${params.toString()}`);
  if (!res.ok) throw new Error('Không tải được lịch sử dòng tiền');
  return res.json();
}

// ── Staking event tracking API ───────────────────────────────────────────
export async function getStakingAlerts({ days, chain, direction } = {}) {
  const params = new URLSearchParams();
  if (days) params.set('days', days);
  if (chain) params.set('chain', chain);
  if (direction) params.set('direction', direction);
  const res = await apiFetch(`${BASE}/staking/alerts?${params}`);
  if (!res.ok) throw new Error('Không tải được cảnh báo staking');
  return res.json();
}

export async function getStakingHealth() {
  const res = await apiFetch(`${BASE}/staking/health`);
  if (!res.ok) throw new Error('Không tải được trạng thái listener staking');
  return res.json();
}

export async function getChainStakingStats() {
  const res = await apiFetch(`${BASE}/staking/chain-stats`);
  if (!res.ok) throw new Error('Không tải được thống kê staking on-chain');
  return res.json();
}

export async function getStakingVolumeSummary(days = 7) {
  const res = await apiFetch(`${BASE}/staking/volume-summary?days=${days}`);
  if (!res.ok) throw new Error('Không tải được tổng hợp dòng tiền staking');
  return res.json();
}

export async function getStakingDailySummary(days = 14, chain, asset) {
  const params = new URLSearchParams({ days: String(days) });
  if (chain) params.set('chain', chain);
  if (asset) params.set('asset', asset);
  const res = await apiFetch(`${BASE}/staking/daily-summary?${params}`);
  if (!res.ok) throw new Error('Không tải được lịch sử tổng hợp staking theo ngày');
  return res.json();
}

export async function getChainStakingHistory(days = 30, chain) {
  const params = new URLSearchParams({ days: String(days) });
  if (chain) params.set('chain', chain);
  const res = await apiFetch(`${BASE}/staking/chain-stats-history?${params}`);
  if (!res.ok) throw new Error('Không tải được lịch sử tỉ lệ staking/tổng cung');
  return res.json();
}

// ── AsterDEX new-listing monitor API ─────────────────────────────────────
export async function getAsterMonitorAlerts({ days, stage } = {}) {
  const params = new URLSearchParams();
  if (days) params.set('days', days);
  if (stage) params.set('stage', stage);
  const res = await apiFetch(`${BASE}/aster-monitor/alerts?${params}`);
  if (!res.ok) throw new Error('Không tải được cảnh báo AsterDEX');
  return res.json();
}

export async function getAsterMonitorHealth() {
  const res = await apiFetch(`${BASE}/aster-monitor/health`);
  if (!res.ok) throw new Error('Không tải được trạng thái listener AsterDEX');
  return res.json();
}

// Layer 3 — symbols detected via AsterDEX's own public exchangeInfo API
// (more reliable than the on-chain layers above — see backend notes).
export async function getAsterExchangeAlerts({ days } = {}) {
  const params = new URLSearchParams();
  if (days) params.set('days', days);
  const res = await apiFetch(`${BASE}/aster-monitor/exchange-alerts?${params}`);
  if (!res.ok) throw new Error('Không tải được cảnh báo exchangeInfo AsterDEX');
  return res.json();
}

// ── HyperLiquid new-listing monitor API ──────────────────────────────────
export async function getHyperliquidMonitorAlerts({ days, marketType } = {}) {
  const params = new URLSearchParams();
  if (days) params.set('days', days);
  if (marketType) params.set('marketType', marketType);
  const res = await apiFetch(`${BASE}/hyperliquid-monitor/alerts?${params}`);
  if (!res.ok) throw new Error('Không tải được cảnh báo HyperLiquid');
  return res.json();
}

export async function getHyperliquidMonitorHealth() {
  const res = await apiFetch(`${BASE}/hyperliquid-monitor/health`);
  if (!res.ok) throw new Error('Không tải được trạng thái monitor HyperLiquid');
  return res.json();
}

export async function getHyperliquidAuction() {
  const res = await apiFetch(`${BASE}/hyperliquid-monitor/auction`);
  if (!res.ok) throw new Error('Không tải được trạng thái đấu giá HyperLiquid');
  return res.json();
}

// Early, unconfirmed ticker registrations (third-party HypurrScan feed) —
// may appear minutes/hours before the authoritative alerts above.
export async function getHyperliquidDeployFeed({ days } = {}) {
  const params = new URLSearchParams();
  if (days) params.set('days', days);
  const res = await apiFetch(`${BASE}/hyperliquid-monitor/deploy-feed?${params}`);
  if (!res.ok) throw new Error('Không tải được feed đăng ký token sớm');
  return res.json();
}

// ── Asset flow tracing ─────────────────────────────────────────────────────
export async function getFlowGraph({ seeds, chain, depth, from, to, forceRefresh }) {
  const res = await apiFetch(`${BASE}/flow-graph`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ seeds, chain, depth, from, to, forceRefresh }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Không truy vết được dòng tài sản');
  return data;
}

export async function getAddressBalance(address, chain) {
  const res = await apiFetch(`${BASE}/flow-graph/balance?address=${encodeURIComponent(address)}&chain=${encodeURIComponent(chain)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Không lấy được số dư ví');
  return data;
}

export async function getTreasuryWatchCases() {
  const res = await apiFetch(`${BASE}/treasury-watch`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Không lấy được danh sách case');
  return data;
}

export async function getTreasuryWatchCase(slug) {
  const res = await apiFetch(`${BASE}/treasury-watch/${encodeURIComponent(slug)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Không lấy được dữ liệu case');
  return data;
}

export async function getNearFlow({ seeds, depth, from, to, forceRefresh }) {
  const res = await apiFetch(`${BASE}/near-flow`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ seeds, depth, from, to, forceRefresh }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Không truy vết được dòng tài sản NEAR');
  return data;
}

// ── Technical Analysis API ───────────────────────────────────────────────

export async function listTechnical({ date, trend, symbol } = {}) {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (trend) params.set('trend', trend);
  if (symbol) params.set('symbol', symbol);
  const qs = params.toString();
  const res = await apiFetch(`${BASE}/technical${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error('Không tải được dữ liệu phân tích kỹ thuật');
  return res.json();
}

export async function getTechnicalMeta() {
  const res = await apiFetch(`${BASE}/technical/meta`);
  if (!res.ok) throw new Error('Không tải được bộ lọc phân tích kỹ thuật');
  return res.json();
}

export async function getTechnicalKlines(symbol) {
  const res = await apiFetch(`${BASE}/technical/${encodeURIComponent(symbol)}/klines`);
  if (!res.ok) throw new Error('Không tải được dữ liệu nến');
  return res.json();
}

export async function getTechnicalHistory(symbol, { limit, dateFrom, dateTo, slim } = {}) {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit);
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  if (slim) params.set('slim', '1');
  const qs = params.toString();
  const res = await apiFetch(`${BASE}/technical/${encodeURIComponent(symbol)}/history${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error('Không tải được lịch sử phân tích');
  return res.json();
}

export async function getExchangeFlows() {
  const res = await apiFetch(`${BASE}/technical/exchange-flows`);
  if (!res.ok) throw new Error('Không tải được dữ liệu nạp/rút sàn');
  return res.json();
}

export async function getContractAuditChains() {
  const res = await apiFetch(`${BASE}/contract-audit/chains`);
  if (!res.ok) throw new Error('Không tải được danh sách chain');
  return res.json();
}

export async function auditContract(chain, address) {
  const params = new URLSearchParams({ chain, address });
  const res = await apiFetch(`${BASE}/contract-audit?${params}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || 'Không kiểm tra được contract này');
    err.code = body.code; // translated on the page via audit.err.<code>
    throw err;
  }
  return res.json();
}

export async function getRecentContractAudits(limit = 20) {
  const res = await apiFetch(`${BASE}/contract-audit/recent?limit=${limit}`);
  if (!res.ok) throw new Error('Không tải được lịch sử tra cứu');
  return res.json();
}
